import React, { FC, useContext } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import { createMarkup } from '@agria/utils/src/createMarkup';
import { Stack } from '../stack/Stack';
import { Button, ButtonProps } from '../button/Button';
import { Typography, TypographyComponents } from '../typography/Typography';
import { Image } from '../Image/Image';
import { IconList, IconListItemsProps } from '../iconList/IconList';
import { Cancel } from '../icon';
import { SectionContext } from '../../context/SectionContext';
import tokens from './tokens';

// Interface

export interface PromoBannerProps {
  backgroundColor?: string;
  button?: ButtonProps;
  buttonOne?: ButtonProps;
  buttonTwo?: ButtonProps;
  description?: string;
  children?: React.ReactNode;
  crossListItems?: IconListItemsProps[];
  footerText?: string | React.ReactNode;
  iconColor?: string;
  image?: { image: { localFile: ImageDataLike } };
  imageAlignment?: 'Image Left' | 'Image Right';
  imageReverse?: boolean;
  imageRight?: boolean;
  heading: string;
  headingTypographyComponent?: TypographyComponents;
  subHeading?: string;
  tickListItems?: IconListItemsProps[];
}

interface StyledPromoBannerProps {
  imageRight: boolean;
  sectionBackground?: string;
}

interface StyledBodyProps {
  backgroundColor?: string;
  sectionBackground?: string;
}

interface StyledImageInterface {
  imageReverse?: boolean;
}
// Styles

const imageWidth = '480px';

const StyledPromoBanner = styled.div<StyledPromoBannerProps>`
  ${({ theme: { media, borderRadii, colors }, imageRight }) => css`
    border-radius: ${borderRadii[tokens.borderRadius]};
    background-color: ${colors.utilityWhite};
    overflow: hidden;
    .gatsby-image-wrapper-constrained {
      height: 100%;
      width: 100%;
    }
    @media (min-width: ${media.medium}) {
      display: flex;
      flex-direction: row-reverse;
      justify-content: flex-end;

      ${imageRight &&
      css`
        flex-direction: row;
      `};
    }
  `};
`;

const StyledImage = styled.div<StyledImageInterface>`
  ${({ theme: { media }, imageReverse }) => css`
    ${imageReverse &&
    css`
      transform: scaleX(-1);
    `}

    @media (min-width: ${media.medium}) {
      position: relative;
      flex-basis: ${imageWidth};

      img {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
      }
    }

    @media (min-width: ${media.large}) {
      min-width: ${imageWidth};
    }
  `};
`;

const StyledBody = styled(Stack)<StyledBodyProps>`
  ${({
    theme: { space, media, colors },
    sectionBackground,
    backgroundColor,
  }) => css`
    padding: ${space.medium};
    width: 100%;
    background: ${colors[backgroundColor] ||
    (sectionBackground === 'utilityWhite'
      ? colors.utilityBackground
      : colors.utilityWhite)};

    @media (min-width: ${media.large}) {
      flex-basis: content;

      padding: ${space.fluidXLarge};
    }
  `};
`;

const StyledButtonContainer = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.small};
    flex-wrap: wrap;

    @media (min-width: ${media.medium}) {
      flex-direction: row;
      justify-content: flex-start;
      gap: ${space.medium};
    }
  `};
`;

const StyledButton = styled(Button)`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.medium}) {
      width: 100%;
    }
  `};
`;

// JSX

export const PromoBanner: FC<PromoBannerProps> = ({
  backgroundColor,
  button,
  buttonOne,
  buttonTwo,
  children,
  crossListItems,
  description,
  footerText,
  heading,
  headingTypographyComponent,
  iconColor,
  image,
  imageAlignment = 'Image Left',
  imageReverse = false,
  imageRight = false,
  subHeading,
  tickListItems,
}) => {
  const { sectionBackground } = useContext(SectionContext);

  const imageOnRight = imageRight || imageAlignment === 'Image Right';
  const imageLocal = image?.image?.localFile;
  return (
    <StyledPromoBanner imageRight={imageOnRight}>
      <StyledBody
        backgroundColor={backgroundColor}
        sectionBackground={backgroundColor ? undefined : sectionBackground}
      >
        <Stack gap="xSmall">
          {heading && (
            <Typography
              component={headingTypographyComponent || 'h3'}
              variant="headingMedium"
            >
              {heading}
            </Typography>
          )}
          {subHeading && (
            <Typography component="p" variant="bodyLarge">
              {subHeading}
            </Typography>
          )}
        </Stack>
        {description && <Typography>{createMarkup(description)}</Typography>}
        {tickListItems && (
          <IconList items={tickListItems} iconColor={iconColor} />
        )}
        {crossListItems && (
          <IconList
            items={crossListItems}
            icon={<Cancel color="brandOne100" />}
          />
        )}
        {children && <div>{children}</div>}
        {button && <StyledButton {...button} />}
        {(buttonOne || buttonTwo) && (
          <StyledButtonContainer>
            {buttonOne && <StyledButton {...buttonOne} />}
            {buttonTwo && <StyledButton variant="secondary" {...buttonTwo} />}
          </StyledButtonContainer>
        )}
        {footerText &&
          (typeof footerText === 'string' ? (
            <Typography variant="bodySmall" color={tokens.color}>
              {footerText}
            </Typography>
          ) : (
            footerText
          ))}
      </StyledBody>
      {image && (
        <StyledImage imageReverse={imageReverse}>
          {imageLocal ? (
            <Image src={imageLocal} alt="" />
          ) : (
            <Image src={image} alt="" />
          )}
        </StyledImage>
      )}
    </StyledPromoBanner>
  );
};

export default PromoBanner;
